// ============================================================================
// AVATAR - Componente reutilizable foto-o-iniciales
// ============================================================================
// Muestra la foto de perfil del usuario (usuarios.avatar) cuando existe, y
// degrada a las iniciales sobre un color estable derivado del nombre cuando no.
// Usado en: página de perfil, chip del header y (tras el backend) comentarios.
// ============================================================================

import React, { useState, useEffect } from 'react';
import appConfig from '../config/appConfig';
import '../styles/avatar.css';

// Paleta de fondos para las iniciales. Cohesiva con la marca (lavanda + pasteles
// neutros), siempre con texto #111 legible encima.
const PALETTE = [
  '#d3caff', // lavanda marca
  '#cfe8d8', // verde suave
  '#ffe0c2', // melocotón
  '#cfe3ff', // azul suave
  '#f6cfe0', // rosa
  '#e6dab8', // arena
  '#cdeceb', // turquesa
  '#e2cdf2', // violeta
  '#ffd6d6', // coral
  '#d9dee8', // gris azulado
];

export function getUserInitials(name) {
  if (!name) return 'U';
  return name
    .trim()
    .split(/\s+/)
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function colorFor(name) {
  if (!name) return PALETTE[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

// Convierte la ruta guardada en usuarios.avatar en una URL servible.
// Acepta URLs absolutas / data: / blob: tal cual; las rutas relativas
// (p.ej. "uploads/avatars/x.jpg") se resuelven contra el dominio del tenant.
export function resolveAvatarUrl(src) {
  if (!src) return null;
  if (/^(https?:|data:|blob:)/.test(src)) return src;
  const base = appConfig.getAll()?.BASE_DOMAIN || '';
  const clean = src.replace(/^\/+/, '');
  const path = clean.startsWith('task/')
    ? clean
    : clean.startsWith('backend/')
    ? `task/${clean}`
    : `task/backend/${clean}`;
  return `${base}/${path}`;
}

/**
 * @param {string}  name   Nombre del usuario (para iniciales y color)
 * @param {string}  src    Ruta/URL del avatar (usuarios.avatar). Opcional.
 * @param {number}  size   Diámetro en px (default 36)
 * @param {string}  bg     Color de fondo fijo para las iniciales (opcional;
 *                         si se omite, se deriva del nombre). Útil para
 *                         mantener el lavanda de marca en el header.
 * @param {string}  className  Clases extra
 * @param {string}  title  title/tooltip (default: name)
 */
const Avatar = ({ name, src, size = 36, bg, className = '', title }) => {
  const [errored, setErrored] = useState(false);
  // Reiniciar el estado de error cuando cambia la imagen (p.ej. tras subir una
  // nueva tras un fallo previo): si no, se quedaría mostrando iniciales.
  useEffect(() => { setErrored(false); }, [src]);
  const url = errored ? null : resolveAvatarUrl(src);
  const dim = typeof size === 'number' ? `${size}px` : size;
  // Iniciales al 40 % del diámetro, con suelo de legibilidad (patrón 13 del
  // rediseño responsive 2026-10): 11 px desde 24 px de avatar y 10 px como
  // mínimo absoluto en los más pequeños. El círculo no cambia de tamaño.
  const fontSize = typeof size === 'number'
    ? `${Math.max(size >= 24 ? 11 : 10, Math.round(size * 0.4 * 10) / 10)}px`
    : `max(11px, calc(${dim} * 0.4))`;
  const baseStyle = { width: dim, height: dim, fontSize };

  if (url) {
    return (
      <span
        className={`jv-avatar jv-avatar-img ${className}`}
        style={baseStyle}
        title={title || name}
      >
        <img src={url} alt={name || 'avatar'} onError={() => setErrored(true)} />
      </span>
    );
  }

  return (
    <span
      className={`jv-avatar ${className}`}
      style={{ ...baseStyle, background: bg || colorFor(name) }}
      title={title || name}
    >
      {getUserInitials(name)}
    </span>
  );
};

export default Avatar;
